import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/Authcontext';
import api from '../utils/api';
import API from '../utils/apiconfig';

const PLAN_OPTIONS = [
  {
    planId: 'emi',
    planType: 'monthly_emi',
    title: 'Monthly EMI Plan',
    badge: 'Flexible Payment',
    badgeClass: 'bg-violet-100 text-violet-800',
    cardRing: 'ring-violet-200/80 hover:ring-violet-300',
    accentBar: 'from-violet-500 to-indigo-500',
    payNowLabel: '₹500',
    payNowSub: 'first month only',
    amount: '₹500 / month',
    duration: '10 months',
    total: '₹5,000 total',
    whoFor:
      'Best if you want to pay month by month. You pay only the current month\'s EMI each time.',
    payStructure: [
      'Today (1st payment): pay ₹500 only',
      'Months 2–10: pay ₹500 each month when due',
      'Same total as full settlement — spread across 10 payments',
    ],
    notThisPlan: 'Do not choose this if you are paying ₹5,000 in one shot today.',
    points: [
      'Scan the website QR and pay ₹500 for your first month.',
      'Upload payment proof; admin verifies before membership activates.',
      'Each following month, pay the next ₹500 EMI when the payment window opens.',
    ],
    buttonLabel: 'Choose Monthly EMI',
    buttonStyle:
      'bg-gradient-to-r from-violet-500 to-indigo-500 hover:from-violet-600 hover:to-indigo-600 text-white shadow-lg shadow-indigo-300/60',
    confirm: {
      headline: 'Monthly EMI Plan',
      payNow: '₹500',
      payNowDetail: 'This is your first month payment only — not the full ₹5,000.',
      structureTitle: 'How you will pay',
      structureLines: [
        'Pay ₹500 now for month 1',
        'Pay ₹500 each month for months 2 through 10',
        '10 payments × ₹500 = ₹5,000 total membership',
      ],
      warning:
        'If you already paid ₹5,000 in one transaction, go back and choose Full Settlement instead.',
    },
  },
  {
    planId: 'full',
    planType: 'full_settlement',
    title: 'Full Settlement in 1st Month',
    badge: 'Pay once',
    badgeClass: 'bg-cyan-100 text-cyan-900',
    cardRing: 'ring-cyan-200/80 hover:ring-cyan-300',
    accentBar: 'from-cyan-500 to-teal-500',
    payNowLabel: '₹5,000',
    payNowSub: 'one payment today',
    amount: '₹5,000 one-time',
    duration: '10-month plan · one payment',
    total: 'All 10 months upfront',
    whoFor:
      'Best if you want to finish payment today. One transfer covers the entire 10-month plan.',
    payStructure: [
      'Today: pay the full ₹5,000 in a single transaction',
      'No monthly EMI payments after admin approval',
      'Same ₹5,000 total as 10 × ₹500 EMI',
    ],
    notThisPlan: 'Do not choose this if you only paid ₹500 today — that is the EMI plan.',
    points: [
      'Scan the website QR and pay the complete ₹5,000 amount.',
      'Upload proof showing the full amount; admin verifies before activation.',
      'Your 10-month plan runs without monthly payment reminders.',
    ],
    buttonLabel: 'Choose Full Settlement',
    buttonStyle:
      'bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white shadow-lg shadow-cyan-300/60',
    confirm: {
      headline: 'Full Settlement (pay once)',
      payNow: '₹5,000',
      payNowDetail: 'You must pay the entire amount in one payment — not ₹500.',
      structureTitle: 'How you will pay',
      structureLines: [
        'Pay ₹5,000 in a single transaction today',
        'Covers all 10 months of membership upfront',
        'No separate ₹500 monthly payments after approval',
      ],
      warning:
        'If you only paid ₹500, go back and choose Monthly EMI instead. Admin will reject a ₹500 proof on this plan.',
    },
  },
];

function PlanConfirmDialog({ plan, loading, onConfirm, onCancel }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  if (!plan) return null;

  const { confirm } = plan;
  const isEmi = plan.planId === 'emi';

  return (
    <div
      className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-4 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="plan-confirm-title"
    >
      <button
        type="button"
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-[2px]"
        aria-label="Close"
        onClick={onCancel}
      />
      <div className="relative w-full max-w-lg rounded-2xl sm:rounded-3xl bg-white shadow-2xl ring-1 ring-slate-200 overflow-hidden max-h-[90vh] overflow-y-auto">
        <div className={`h-1.5 bg-gradient-to-r ${plan.accentBar}`} aria-hidden />
        <div className="p-6 sm:p-7">
          <p className="text-[11px] font-bold tracking-[0.18em] text-indigo-600 uppercase">
            Confirm your plan
          </p>
          <h2
            id="plan-confirm-title"
            className="mt-2 text-xl sm:text-2xl font-black text-slate-900"
            style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}
          >
            {confirm.headline}
          </h2>
          <p className="mt-2 text-sm text-slate-600 leading-relaxed">
            Please read carefully before you pay and upload proof. The amount you pay must match
            this plan.
          </p>

          <div
            className={`mt-5 rounded-2xl border p-4 ${
              isEmi
                ? 'border-violet-200 bg-gradient-to-br from-violet-50/90 to-white'
                : 'border-cyan-200 bg-gradient-to-br from-cyan-50/90 to-white'
            }`}
          >
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Amount to pay now
            </p>
            <p className="mt-1 text-4xl font-black text-slate-900">{confirm.payNow}</p>
            <p className="mt-1 text-sm font-medium text-slate-700">{confirm.payNowDetail}</p>
          </div>

          <div className="mt-5">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
              {confirm.structureTitle}
            </p>
            <ul className="mt-2 space-y-2">
              {confirm.structureLines.map((line) => (
                <li key={line} className="flex items-start gap-2 text-sm text-slate-700">
                  <span
                    className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
                      isEmi ? 'bg-violet-500' : 'bg-cyan-500'
                    }`}
                    aria-hidden
                  />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-5 rounded-xl border border-amber-200/90 bg-amber-50/90 px-4 py-3">
            <p className="text-xs font-bold uppercase tracking-wide text-amber-800">Important</p>
            <p className="mt-1 text-sm leading-relaxed text-amber-950/90">{confirm.warning}</p>
          </div>

          <div className="mt-6 flex flex-col-reverse sm:flex-row gap-3">
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="flex-1 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
            >
              Go back
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={loading}
              className={`flex-1 rounded-xl px-4 py-3 text-sm font-extrabold text-white disabled:opacity-70 ${plan.buttonStyle}`}
            >
              {loading ? 'Loading…' : `Yes, pay ${confirm.payNow} & continue`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Plans() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loadingPlanId, setLoadingPlanId] = useState(null);
  const [confirmPlan, setConfirmPlan] = useState(null);

  const redirectWithSnack = (message) => {
    navigate('/dashboard', { state: { snackMessage: message } });
  };

  const proceedToPayment = async (plan) => {
    setLoadingPlanId(plan.planId);
    try {
      const response = await api.get(API.PLAN_BY_TYPE(plan.planType));
      const data = response.data;

      if (!data?.success) {
        redirectWithSnack(data?.message || 'Unable to proceed with this plan.');
        return;
      }

      if (data.can_enroll === false) {
        redirectWithSnack(
          data.enrollment_blocked_reason ||
            data.message ||
            'You already have an active plan on this account.'
        );
        return;
      }

      const planDetails = data.plan || {};
      setConfirmPlan(null);
      navigate('/payment', {
        state: {
          selectedPlan: plan.planId,
          planName: planDetails.plan_name || plan.title,
          amountLabel: planDetails.amount_label || plan.payNowLabel,
          amount: planDetails.amount || planDetails.amt,
          currentPlan: planDetails.current_plan,
          catalogPlanId: data.catalog_plan_id,
          planConfirmed: true,
        },
      });
    } catch (err) {
      const payload = err.response?.data;
      if (payload?.success === false) {
        redirectWithSnack(payload.message || 'Unable to proceed with this plan.');
        return;
      }
      redirectWithSnack(
        payload?.message ||
          payload?.error ||
          'Something went wrong. Please try again.'
      );
    } finally {
      setLoadingPlanId(null);
    }
  };

  const handleChoosePlan = (plan) => {
    if (!user) {
      navigate('/login');
      return;
    }
    setConfirmPlan(plan);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-indigo-50 to-cyan-100 py-10 sm:py-14 px-4 sm:px-5">
      <div className="max-w-6xl mx-auto">
        {user?.can_one_time_plan_switch ? (
          <div className="mb-8 rounded-2xl border border-violet-200/80 bg-gradient-to-r from-violet-50/95 via-indigo-50/90 to-white px-5 py-4 shadow-sm">
            <p className="text-sm font-semibold text-violet-950">One-time plan change available</p>
            <p className="mt-1 text-sm leading-relaxed text-violet-900/80">
              Your first payment proof was rejected. Pick the plan that matches what you actually
              paid (₹500/month vs ₹5,000 one-time), then upload new proof.
            </p>
          </div>
        ) : null}

        <div className="text-center mb-8 sm:mb-10">
          <p className="text-xs font-bold tracking-[0.22em] text-indigo-600 uppercase mb-3">
            Choose Your Plan
          </p>
          <h1
            className="text-3xl sm:text-5xl font-black text-slate-900 mb-4"
            style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}
          >
            Select a Payment Plan That Fits You
          </h1>
          <p className="text-sm sm:text-base text-slate-600 max-w-3xl mx-auto leading-relaxed">
            Both plans cost ₹5,000 in total. The difference is{' '}
            <strong className="font-semibold text-slate-800">how much you pay today</strong>:
            ₹500 for monthly EMI, or ₹5,000 for full settlement.
          </p>
        </div>

        {/* Quick comparison */}
        <div className="mb-8 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-3xl mx-auto">
          <div className="rounded-xl border border-violet-200/80 bg-violet-50/60 px-4 py-3 text-center sm:text-left">
            <p className="text-[10px] font-bold uppercase tracking-wider text-violet-700">
              Monthly EMI
            </p>
            <p className="mt-0.5 text-lg font-black text-violet-950">Pay ₹500 today</p>
            <p className="text-xs text-violet-900/70">Then ₹500 each month</p>
          </div>
          <div className="rounded-xl border border-cyan-200/80 bg-cyan-50/60 px-4 py-3 text-center sm:text-right">
            <p className="text-[10px] font-bold uppercase tracking-wider text-cyan-800">
              Full settlement
            </p>
            <p className="mt-0.5 text-lg font-black text-cyan-950">Pay ₹5,000 today</p>
            <p className="text-xs text-cyan-900/70">All 10 months in one payment</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          {PLAN_OPTIONS.map((plan) => (
            <article
              key={plan.planId}
              className={`rounded-3xl bg-white/95 backdrop-blur border border-white p-6 sm:p-8 shadow-[0_20px_50px_-20px_rgba(15,23,42,0.35)] ring-1 transition-shadow ${plan.cardRing}`}
            >
              <div className={`h-1 rounded-full bg-gradient-to-r ${plan.accentBar} mb-5`} aria-hidden />

              <div className="flex items-center justify-between gap-3 mb-4">
                <span
                  className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-bold ${plan.badgeClass}`}
                >
                  {plan.badge}
                </span>
                <p className="text-xs text-slate-500 font-semibold">{plan.total}</p>
              </div>

              <h2 className="text-2xl font-black text-slate-900 mb-2">{plan.title}</h2>
              <p className="text-sm text-slate-600 leading-relaxed mb-4">{plan.whoFor}</p>

              <div
                className={`rounded-2xl border px-4 py-3 mb-4 ${
                  plan.planId === 'emi'
                    ? 'border-violet-200 bg-violet-50/50'
                    : 'border-cyan-200 bg-cyan-50/50'
                }`}
              >
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  You pay today
                </p>
                <div className="flex items-baseline gap-2 mt-0.5">
                  <p className="text-3xl font-black text-slate-900">{plan.payNowLabel}</p>
                  <p className="text-sm font-medium text-slate-600">{plan.payNowSub}</p>
                </div>
              </div>

              <div className="mb-4">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">
                  Payment structure
                </p>
                <ul className="space-y-1.5">
                  {plan.payStructure.map((line) => (
                    <li key={line} className="flex items-start gap-2 text-sm text-slate-700">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-slate-400" />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <p className="text-xs text-slate-500 italic border-l-2 border-slate-200 pl-3 mb-4">
                {plan.notThisPlan}
              </p>

              <ul className="space-y-2 text-sm text-slate-600 leading-relaxed">
                {plan.points.map((point) => (
                  <li key={point} className="flex items-start gap-2.5">
                    <span className="mt-1.5 h-2 w-2 rounded-full bg-slate-300 shrink-0" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>

              <button
                type="button"
                disabled={loadingPlanId !== null}
                onClick={() => handleChoosePlan(plan)}
                className={`mt-8 w-full rounded-xl px-5 py-3.5 text-sm font-extrabold transition-all active:scale-[0.99] disabled:opacity-70 disabled:cursor-not-allowed ${plan.buttonStyle}`}
              >
                {loadingPlanId === plan.planId ? 'Loading…' : plan.buttonLabel}
              </button>
            </article>
          ))}
        </div>
      </div>

      <PlanConfirmDialog
        plan={confirmPlan}
        loading={loadingPlanId === confirmPlan?.planId}
        onCancel={() => setConfirmPlan(null)}
        onConfirm={() => confirmPlan && proceedToPayment(confirmPlan)}
      />
    </div>
  );
}

export default Plans;
